import { Head, Link } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import type { SeoMeta } from "@/types"
import { LOCATIONS, LOCATION_SLUGS } from "@/data/directory/registry"

/* ------------------------------------------------------------------ */
/* Icons                                                                */
/* ------------------------------------------------------------------ */

function IconMapPin({ className = "size-5" }: { className?: string }) {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className={className}>
      <path
        fillRule="evenodd"
        d="m9.69 18.933.003.001C9.89 19.02 10 19 10 19s.11.02.308-.066l.002-.001.006-.003.018-.008a5.741 5.741 0 0 0 .281-.14c.186-.096.446-.24.757-.433.62-.384 1.445-.966 2.274-1.765C15.302 14.988 17 12.493 17 9A7 7 0 1 0 3 9c0 3.492 1.698 5.988 3.355 7.584a13.731 13.731 0 0 0 2.273 1.765 11.842 11.842 0 0 0 .976.544l.062.029.018.008.006.003ZM10 11.25a2.25 2.25 0 1 0 0-4.5 2.25 2.25 0 0 0 0 4.5Z"
        clipRule="evenodd"
      />
    </svg>
  )
}

function IconArrowRight({ className = "size-4" }: { className?: string }) {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className={className}>
      <path
        fillRule="evenodd"
        d="M3 10a.75.75 0 0 1 .75-.75h10.638L10.23 5.29a.75.75 0 1 1 1.04-1.08l5.5 5.25a.75.75 0 0 1 0 1.08l-5.5 5.25a.75.75 0 1 1-1.04-1.08l4.158-3.96H3.75A.75.75 0 0 1 3 10Z"
        clipRule="evenodd"
      />
    </svg>
  )
}

/* ------------------------------------------------------------------ */
/* Location card                                                        */
/* ------------------------------------------------------------------ */

function LocationCard({ slug }: { slug: string }) {
  const location = LOCATIONS[slug]
  if (!location) return null

  const previewAreas = location.areas.slice(0, 4)
  const moreCount = location.areas.length - previewAreas.length

  return (
    <Link
      href={`/directory/${slug}`}
      className="group flex flex-col overflow-hidden rounded-xl border border-stone bg-white transition-all hover:border-stone/80 hover:shadow-md"
    >
      <div className="flex-1 p-8">
        <div className="flex items-center gap-3">
          <div className="flex size-10 items-center justify-center rounded-full bg-red/10">
            <IconMapPin className="size-5 text-red" />
          </div>
          <h2 className="font-display text-2xl font-bold text-charcoal">
            {location.name}
          </h2>
        </div>

        <p className="mt-4 text-sm text-slate">{location.description}</p>

        <div className="mt-5 flex flex-wrap gap-1.5">
          {previewAreas.map((a) => (
            <span
              key={a}
              className="rounded-full bg-stone/50 px-2.5 py-0.5 text-[11px] font-medium text-slate"
            >
              {a}
            </span>
          ))}
          {moreCount > 0 && (
            <span className="rounded-full bg-stone/50 px-2.5 py-0.5 text-[11px] font-medium text-warm-grey">
              +{moreCount} more
            </span>
          )}
        </div>

        <p className="mt-4 text-xs text-warm-grey">
          {location.offices.length} offices listed
        </p>
      </div>

      <div className="flex items-center justify-between border-t border-stone px-8 py-4">
        <span className="text-sm font-semibold text-red transition-colors group-hover:text-deep-red">
          Explore {location.name}
        </span>
        <IconArrowRight className="size-4 text-red transition-transform group-hover:translate-x-1" />
      </div>
    </Link>
  )
}

/* ------------------------------------------------------------------ */
/* Page                                                                 */
/* ------------------------------------------------------------------ */

export default function DirectoryIndex({ meta }: { meta: SeoMeta }) {
  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
      </Head>

      {/* Hero */}
      <section className="px-6 py-24 md:py-32">
        <div className="mx-auto max-w-4xl text-center">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">Directory</p>
          <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-charcoal sm:text-5xl md:text-6xl">
            Estate agents directory<span className="text-red">.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate">
            Choose a location to explore and compare the top estate agents in your area.
          </p>
        </div>
      </section>

      {/* Location cards */}
      <section className="border-t border-stone bg-white px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <div className="grid gap-6 sm:grid-cols-2">
            {LOCATION_SLUGS.map((slug) => (
              <LocationCard key={slug} slug={slug} />
            ))}
          </div>
        </div>
      </section>

      {/* SEO copy */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            Finding the right estate agent<span className="text-red">.</span>
          </h2>
          <div className="mt-6 space-y-4 text-base leading-relaxed text-slate">
            <p>
              Choosing the right estate agent can make a real difference - whether you&rsquo;re
              selling a family home, a first-time buy, or a period property. Our directory brings
              together leading agents across the UK&rsquo;s most dynamic markets, from national
              firms to respected local independents.
            </p>
            <p>
              Select a location above to browse offices by area or specialism, read reviews, and
              compare agents side by side. Every agent listed has a physical office and includes
              direct contact details and links to their website. You can also see our{" "}
              <Link href="/best" className="text-red hover:text-deep-red">
                best estate agents rankings
              </Link>{" "}
              or explore{" "}
              <Link href="/areas" className="text-red hover:text-deep-red">
                neighbourhood area guides
              </Link>{" "}
              to learn more about the local property market.
            </p>
          </div>
        </div>
      </section>

      {/* Get listed CTA */}
      <section className="border-t border-stone bg-cream px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <div className="rounded-2xl border border-stone bg-white p-8 text-center shadow-sm md:p-12">
            <div className="mx-auto flex size-14 items-center justify-center rounded-full bg-red/10">
              <svg viewBox="0 0 24 24" fill="none" stroke="#C41E2A" strokeWidth={1.5} className="size-7">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
              </svg>
            </div>
            <h2 className="mt-6 font-display text-2xl font-bold text-charcoal md:text-3xl">
              Want to be listed in our directory<span className="text-red">?</span>
            </h2>
            <p className="mx-auto mt-3 max-w-lg text-base text-slate">
              If you&rsquo;re an estate agent and would like your office featured here,
              we&rsquo;d love to hear from you. Listings are free and include a direct backlink to
              your website.
            </p>
            <div className="mt-8">
              <Link
                href="/contact"
                className="inline-flex items-center justify-center rounded-md bg-red px-6 py-3 text-base font-medium text-white transition-colors hover:bg-deep-red"
              >
                Get in touch
              </Link>
            </div>
            <p className="mt-4 text-sm text-warm-grey">
              Or drop us a line at{" "}
              <a href="mailto:team@quiteright.co.uk" className="font-medium text-red hover:text-deep-red">
                team@quiteright.co.uk
              </a>
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-stone bg-charcoal px-6 py-20">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="font-display text-3xl font-bold text-cream md:text-4xl">
            Are you an estate agent<span className="text-red">?</span>
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base text-warm-grey">
            Turn your property listings into scroll-stopping video ads. Paste a listing, pick a style,
            and publish across every social platform in under a minute.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              href="/demo"
              className="inline-flex w-full items-center justify-center rounded-md bg-red px-6 py-3 text-base font-medium text-white transition-colors hover:bg-deep-red sm:w-auto"
            >
              Request a demo
            </Link>
            <Link
              href="/how-it-works"
              className="inline-flex w-full items-center justify-center rounded-md border border-slate bg-charcoal px-6 py-3 text-base font-medium text-cream transition-colors hover:bg-slate sm:w-auto"
            >
              See how it works
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  )
}
